/**
 * Resumes.tsx
 * 
 * This page handles the resume management functionality, including:
 * 1. Uploading resumes (local files and Google Drive)
 * 2. Viewing and managing uploaded resumes
 * 3. Tailoring resumes to job descriptions using AI
 * 4. Providing interactive suggestions for resume improvements
 */

// src/pages/Resumes.tsx
import PageHeader from '../components/PageHeader';
import { PAGE_HEADER_HEIGHT } from '../constants/layout';
import React, { useState, useEffect, useRef, useMemo, useCallback, type JSX } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabase';
import { IoAddOutline, IoCloudUploadOutline, IoTrashOutline, IoDocumentTextOutline, IoChevronDownOutline, IoChevronUpOutline, IoCloseOutline } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { User } from '@supabase/supabase-js';
import { useOutletContext, useNavigate } from 'react-router-dom';
import ReactMarkdown, { Components } from 'react-markdown';
import { createLogger } from '../utils/logger';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocumentProxy, PDFPageProxy, PDFDocumentLoadingTask, RenderTask } from 'pdfjs-dist';
import FileViewerModal from '../components/FileViewerModal';
import { useCachedData } from '../hooks/useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { cleanMarkdown } from '../utils/markdownUtils';
import { Document, pdfjs } from 'react-pdf';
import { Note, FilterItem } from '../types/notes';
import { extractTags } from '../utils/tagUtils';
import { ModernFileService } from '../utils/modernFileService';
import { FileMetadata } from '../types/files';
import type { TextItem, TextMarkedContent } from 'pdfjs-dist/types/src/display/api';
import type { ReactNode } from 'react';
import ThumbnailPreview from '../components/ThumbnailPreview';
import PersistentFilterModal from '../components/EnhancedFilterModal/PersistentFilterModal';
import { useBusinessMetrics } from '../hooks/useStatsigFeatures';
import { useAuthState } from '../hooks/useAuthState';
import { useBannerAutoRead } from '../hooks/useBannerAutoRead';

const logger = createLogger({ module: 'Resumes', level: 'info' });

// Configure PDF.js workers
if (typeof window !== 'undefined') {
  // Use a relative path for PDF.js workers to avoid CSP issues
  const workerUrl = '/pdf.worker.min.mjs';
  
  // For pdfjs-dist 5.x - configure worker
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  
  // For react-pdf
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
}

/**
 * Helper function to clean text for better matching in search operations
 * Removes special characters, extra spaces, and converts to lowercase
 */
const cleanTextForMatching = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Custom markdown component configurations for consistent styling
 * These components are used when rendering markdown content in the suggestions panel
 */
const markdownComponents: Components = {
  p: ({ children, ...props }) => (
    <p className="my-0.5 text-sm" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }) => (
    <ul className="my-0.5 list-disc list-inside text-sm" {...props}>{children}</ul>
  ),
  li: ({ children, ...props }) => (
    <li className="my-0 text-sm" {...props}>{children}</li>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-sm" {...props}>{children}</strong>
  ),
  h1: ({ children, ...props }) => (
    <h1 className="text-lg font-bold my-1" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-base font-semibold my-1" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-sm font-semibold my-1" {...props}>{children}</h3>
  ),
};

/**
 * Type guard to differentiate between TextItem and TextMarkedContent
 * Used in PDF processing to ensure we're working with text elements that have string content
 */
const isTextItem = (item: TextItem | TextMarkedContent): item is TextItem => {
  return 'str' in item && 'transform' in item;
};

/**
 * Interface definitions for data structures used throughout the page
 */
interface Resume extends FileMetadata {
  external_download_url?: string;
  external_web_url?: string;
  external_file_id?: string;
}

/**
 * CareerCard interface representing career experience data
 * Used for providing additional context when tailoring resumes
 */
interface CareerCard {
  cardid: string;
  card_data: {
    title: string;
    type: string;
    overview?: string;
    custom_tags?: string[];
    date_tags?: string[];
    [key: string]: any;
  };
}

/**
 * Transforms notes and career cards into a consistent FilterItem format
 * Used by the filter modal for selecting additional context when tailoring resumes
 */
const transformToFilterItem = (item: any, type: 'note' | 'card'): FilterItem => {
  if (type === 'note') {
    return {
      id: item.id,
      title: item.title || 'Untitled Note',
      type: 'note',
      custom_tags: item.custom_tags || [],
      date_tags: item.date_tags || []
    };
  } else {
    // Career cards can have either 'id' or 'cardid' based on the interface
    const cardId = item.cardid || item.id;
    const title = item.card_data?.title || item.title || 'Untitled Card';
    
    return {
      id: cardId,
      title: title,
      type: 'card',
      custom_tags: item.card_data?.custom_tags || item.custom_tags || [],
      date_tags: item.card_data?.date_tags || item.date_tags || [],
      card_data: {
        custom_tags: item.card_data?.custom_tags || item.custom_tags || [],
        date_tags: item.card_data?.date_tags || item.date_tags || []
      }
    };
  }
};

/**
 * Interface definitions for suggestion data structures
 * These are used to represent and display AI-generated suggestions
 */
interface SuggestionItemData {
  text: string;
  highlights: (string | HighlightText)[];
  area: string;
}

interface HighlightText {
  text?: string;
  name?: string;
  description?: string;
  [key: string]: string | undefined;
}

interface SuggestionItemProps {
  children: ReactNode;
  highlights: (string | HighlightText)[];
  area: string;
  onHighlight: (highlights: (string | HighlightText)[], area: string) => void;
}

/**
 * SuggestionItem component displays individual suggestions with highlighting functionality
 * Handles user interactions and displays tooltips for relevant resume sections
 */
const SuggestionItem: React.FC<SuggestionItemProps> = ({ children, area, highlights, onHighlight }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(() => {
    onHighlight(highlights, area);
  }, [highlights, area, onHighlight]);

  const handleMouseEnter = useCallback(() => {
    setShowTooltip(true);
  }, []);

  const handleMouseLeave = useCallback((event: React.MouseEvent) => {
    try {
      const relatedTarget = event.relatedTarget as Node | null;
      if (itemRef.current && relatedTarget instanceof Node && !itemRef.current.contains(relatedTarget)) {
        setShowTooltip(false);
      }
    } catch (error) {
      logger.error('Error in handleMouseLeave:', error);
      setShowTooltip(false);
    }
  }, []);

  return (
    <div
      ref={itemRef}
      className="relative group"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="cursor-pointer p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <ReactMarkdown
          components={markdownComponents}
          skipHtml={true}
          remarkPlugins={[]}
          allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3']}
        >
          {typeof children === 'string' ? children : ''}
        </ReactMarkdown>
      </div>
      {showTooltip && highlights.length > 0 && (
        <SuggestionTooltip highlights={highlights} />
      )}
    </div>
  );
};

/**
 * Interface for handling suggestion group properties
 * Used to organize and display related suggestions
 */
interface SuggestionGroupProps {
  title: string;
  items: Array<{
    text: string;
    highlights: (string | HighlightText)[];
    area: string;
  }>;
  onHighlight: (highlights: (string | HighlightText)[], area: string) => void;
  darkMode?: boolean;
}

/**
 * SuggestionGroup component organizes related suggestions into collapsible sections
 * Provides a consistent layout for different types of suggestions
 */
const SuggestionGroup: React.FC<SuggestionGroupProps> = ({ title, items, onHighlight, darkMode }) => {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <div className="space-y-2">
        {items.map((item, itemIndex) => (
          <SuggestionItem
            key={`item-${itemIndex}`}
            area={item.area}
            highlights={item.highlights}
            onHighlight={onHighlight}
          >
            {item.text}
          </SuggestionItem>
        ))}
      </div>
    </div>
  );
};

interface HighlightPosition {
  element: HTMLElement;
  text: string;
}

interface TextPosition {
  text: string;
  positions: HighlightPosition[];
}

type HandleHighlightFunction = (
  highlights: (string | HighlightText)[],
  positions?: TextPosition[]
) => void;

interface SuggestionsPanelProps {
  suggestions: string;
  onHighlight: (highlights: (string | HighlightText)[], area: string) => void;
  darkMode?: boolean;
}

interface Section {
  title: string;
  items: SuggestionItemData[];
}

interface CurrentItem {
  text: string;
  highlights: (string | HighlightText)[];
  area: string;
}

/**
 * SuggestionsPanel component displays suggestions in a structured format
 * Allows for highlighting and grouping of related suggestions
 */
const SuggestionsPanel: React.FC<SuggestionsPanelProps> = ({ suggestions, onHighlight, darkMode }) => {
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const processedSuggestions = useMemo(() => {
    logger.debug('Processing suggestions:', { suggestions });
    const sections: Section[] = [];
    let currentSection: Section | null = null;
    let currentItem: CurrentItem | null = null;

    const lines = suggestions.split('\n');
    
    lines.forEach((line: string) => {
      const trimmedLine = line.trim();
      
      if (line.startsWith('#')) {
        // If we have a pending item, add it to the current section
        if (currentItem && currentSection) {
          currentSection.items.push(currentItem);
          currentItem = null;
        }
        
        // If we have a pending section, add it to sections
        if (currentSection) {
          sections.push(currentSection);
        }
        
        // Start new section
        currentSection = {
          title: line.replace(/^#+\s/, ''),
          items: []
        };
        logger.debug('Found section:', { title: currentSection.title });
      } 
      // Check for highlight tags
      else if (trimmedLine.includes('{{highlight:')) {
        if (currentItem && currentSection) {
          const matches = trimmedLine.match(/\{\{highlight:(.*?)\}\}/);
          if (matches) {
            try {
              const highlightData = matches[1];
              logger.debug('Found highlight data:', { highlightData });
              const parsedHighlights = JSON.parse(highlightData);
              currentItem.highlights = Array.isArray(parsedHighlights) ? parsedHighlights : [];
              logger.debug('Parsed highlights:', { highlights: currentItem.highlights });
              
              // Add the completed item to the section
              currentSection.items.push(currentItem);
              currentItem = null;
            } catch (error) {
              logger.error('Error parsing highlights:', error);
            }
          }
        }
      }
      // Check for area tags
      else if (trimmedLine.startsWith('{{area:')) {
        // If we have a pending item, add it to the current section
        if (currentItem && currentSection) {
          currentSection.items.push(currentItem);
          currentItem = null;
        }
        
        const areaMatch = trimmedLine.match(/\{\{area:(.*?)\}\}/);
        if (areaMatch && currentSection) {
          currentItem = {
            text: '',
            highlights: [],
            area: areaMatch[1].trim()
          };
        }
      }
      // Process content lines
      else if (trimmedLine && !trimmedLine.startsWith('{{/area}}') && currentItem) {
        // Append content to current item
        const cleanedLine = trimmedLine
          .replace(/\{\{highlight:.*?\}\}/g, '')
          .replace(/^\s*[-*]\s*/, '')
          .trim();
          
        if (cleanedLine) {
          currentItem.text += (currentItem.text ? '\n' : '') + cleanedLine;
        }
      }
    });

    // Handle any remaining items/sections
    if (currentItem && currentSection) {
      currentSection.items.push(currentItem);
    }
    if (currentSection) {
      sections.push(currentSection);
    }

    logger.debug('Processed sections:', { sections });
    return sections;
  }, [suggestions]);

  return (
    <div 
      ref={panelRef}
      className={`space-y-6 p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
    >
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Click on suggestions to highlight relevant areas in your resume
      </div>
      
      {processedSuggestions.map((section, index) => (
        <SuggestionGroup
          key={`section-${index}`}
          title={section.title}
          items={section.items}
          onHighlight={onHighlight}
          darkMode={darkMode}
        />
      ))}
    </div>
  );
};

// Add after imports and before other components
interface TooltipProps {
  highlights: (string | HighlightText)[];
}

/**
 * SuggestionTooltip component displays highlight information when hovering over suggestions
 * Shows the relevant text from the resume that matches the suggestion
 */
const SuggestionTooltip: React.FC<TooltipProps> = ({ highlights }) => {
  const { darkMode } = useDarkMode();

  return (
    <div 
      className={`
        absolute z-100 px-3 py-2 
        text-xs rounded-lg shadow-lg
        max-w-xs whitespace-pre-wrap
        ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-800 text-white'}
      `}
      style={{
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '8px'
      }}
    >
      <div className="relative">
        <div className="font-medium mb-1">Click to highlight:</div>
        {highlights.map((highlight, index) => {
          let displayText = '';
          if (typeof highlight === 'string') {
            displayText = highlight;
          } else if (highlight && typeof highlight === 'object') {
            const highlightObj = highlight as HighlightText;
            displayText = highlightObj.text || 
                        highlightObj.name || 
                        highlightObj.description || 
                        Object.values(highlightObj).find(v => typeof v === 'string') || '';
          }
          
          return (
            <div 
              key={index} 
              className={`
                mt-1 text-sm
                ${darkMode ? 'text-gray-400' : 'text-gray-300'}
              `}
            >
              {displayText}
            </div>
          );
        })}
        <div 
          className={`
            absolute w-2 h-2 transform rotate-45
            ${darkMode ? 'bg-gray-700' : 'bg-gray-800'}
          `}
          style={{
            bottom: '-4px',
            left: '50%',
            marginLeft: '-4px'
          }}
        />
      </div>
    </div>
  );
};

/**
 * Props interface for the Resumes component
 * Defines the server-side rendered data that can be passed to the component
 */
interface ResumesProps {
  initialCareerCards?: any[];
  initialNotes?: any[];
  initialResumes?: any[];
}

/**
 * Resumes component handles the resume management functionality
 */
const Resumes: React.FC<ResumesProps> = ({
  initialCareerCards = [],
  initialNotes = [],
  initialResumes = []
}) => {
  // State management for various features
  const { darkMode } = useDarkMode();
  
  // Statsig analytics hooks
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  
  // Auto-mark banner notifications as read when landing on resumes page
  useBannerAutoRead();
  
  const [resumes, setResumes] = useState<Resume[]>(initialResumes);
  const [user, setUser] = useState<User | null>(null);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [selectedResumeUrl, setSelectedResumeUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [jobDescriptionType, setJobDescriptionType] = useState<'text' | 'url'>('text');
  const [jobDescriptionText, setJobDescriptionText] = useState('');
  const [jobDescriptionUrl, setJobDescriptionUrl] = useState('');
  const [parsedJobDescription, setParsedJobDescription] = useState('');
  const [isParsingUrl, setIsParsingUrl] = useState(false);
  const [suggestions, setSuggestions] = useState('');
  const { openResumeId } = useOutletContext<{ openResumeId?: string }>() || {};
  const [selectedResumeForSuggestions, setSelectedResumeForSuggestions] = useState<Resume | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [jobDescriptionHtml, setJobDescriptionHtml] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [jobUrlError, setJobUrlError] = useState<string | null>(null);
  const [newWindow, setNewWindow] = useState<Window | null>(null);
  const [parsingStatus, setParsingStatus] = useState<string>('');
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [suggestionsStatus, setSuggestionsStatus] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedCareerCards, setSelectedCareerCards] = useState<string[]>([]);
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  const [sectionsVisible, setSectionsVisible] = useState({
    upload: false,
    resumes: true,
    tailor: true
  });
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [signedResumeUrl, setSignedResumeUrl] = useState<string | null>(null);
  const [highlightedArea, setHighlightedArea] = useState<string | null>(null);
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLObjectElement | HTMLIFrameElement>(null);
  const navigate = useNavigate();

  // Add useAuthState hook
  const { user: authUser, isAuthenticated, isLoading: authLoading } = useAuthState();

  // Add new state for enhanced filter items
  const [enhancedFilterItems, setEnhancedFilterItems] = useState<{
    notes: FilterItem[];
    cards: FilterItem[];
  }>({
    notes: [],
    cards: []
  });

  // Add loading state
  const [isLoadingResumes, setIsLoadingResumes] = useState(false);

  // Journey tracking
  const journeyIdRef = useRef<string>('');
  const journeyActiveRef = useRef<boolean>(false);
  const pageLoadStartTimeRef = useRef<number>(performance.now());
  const [pageJourneyId, setPageJourneyId] = useState<string | null>(null);
  const [uploadJourneyId, setUploadJourneyId] = useState<string | null>(null);
  const [tailoringJourneyId, setTailoringJourneyId] = useState<string | null>(null);

  // Fetch unified data (notes and career cards) - using same approach as Home page
  const { data: unifiedData, loading: unifiedLoading } = useCachedData<{
    notes: Note[];
    careerCards: CareerCard[];
  }>(
    async () => {
      // Return empty data if auth is still loading
      if (authLoading) {
        return { notes: [], careerCards: [] };
      }
      
      if (!authUser) throw new Error('No authenticated user');

      // Fetch notes and career cards in parallel for efficiency
      // Note: RLS policies already filter by user, so no need for explicit user_id filter
      const [notesResult, cardsResult] = await Promise.all([
        supabase
          .from('notes')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('career_cards')
          .select('*')
          .order('created_at', { ascending: false })
      ]);

      if (notesResult.error) throw notesResult.error;
      if (cardsResult.error) throw cardsResult.error;


      return {
        notes: notesResult.data || [],
        careerCards: cardsResult.data || []
      };
    },
    {
      cacheKey: 'user-unified-content',
      maxAge: CACHE_DURATIONS.MEDIUM,
      initialData: {
        notes: initialNotes || [],
        careerCards: initialCareerCards || []
      }
    }
  );

  // Extract notes and career cards from unified data
  const notes = unifiedData?.notes || [];
  const careerCards = unifiedData?.careerCards || [];
  const notesLoading = unifiedLoading;
  const careerCardsLoading = unifiedLoading;

  const { showToast } = useToast();

  /**
   * Effect hooks for initialization and data fetching
   */
  useEffect(() => {
    // Start resume management journey when component mounts
    const journeyId = `resume_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    journeyIdRef.current = journeyId;
    journeyActiveRef.current = true;
    setPageJourneyId(journeyId);
    
    // Track user journey start
    trackUserJourney('resume_management_started', {
      journey_id: journeyId,
      entry_point: 'direct',
      has_resumes: resumes.length > 0
    });
    
    // Track page view
    trackFeatureUsage('resumes_page', 'viewed');
    
    // Track page load performance
    const loadTime = performance.now() - pageLoadStartTimeRef.current;
    trackFeatureUsage('page_performance', 'measured', {
      route: '/resumes',
      load_time_ms: Math.round(loadTime),
      first_paint_ms: Math.round(
        performance.getEntriesByType('paint')
          .find(entry => entry.name === 'first-paint')?.startTime || loadTime
      )
    });
    
    return () => {
      // If user navigates away without completing, abandon journey
      if (journeyIdRef.current && journeyActiveRef.current) {
        trackUserJourney('resume_management_abandoned', {
          journey_id: journeyIdRef.current,
          reason: 'page_exit',
          time_spent_ms: performance.now() - pageLoadStartTimeRef.current
        });
        journeyActiveRef.current = false;
      }
    };
  }, [resumes.length]); // Removed trackFeatureUsage and trackUserJourney from dependency array

  useEffect(() => {
    // Update user state from authUser
    setUser(authUser);
  }, [authUser]);

  useEffect(() => {
    if (user) {
      fetchResumes();
    }
  }, [user]);

  useEffect(() => {
    if (openResumeId) {
      const resumeToOpen = resumes.find(resume => resume.id === openResumeId);
      if (resumeToOpen) {
        handleViewResume(resumeToOpen);
      }
    }
  }, [openResumeId, resumes]);

  useEffect(() => {
    // Listen for messages from the opened window
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === 'JOB_DESCRIPTION_CONTENT') {
        logger.debug('Received content from opened window');
        const content = event.data.content;
        
        if (!content || content.length < 50) {
          setJobUrlError('Could not extract meaningful content from the page.');
          return;
        }

        try {
          // Use OpenAI to parse the content
          logger.debug('Sending content to OpenAI for parsing');
          if (!isAuthenticated || !authUser) throw new Error('User not authenticated');
          
          // Keep the authorization header
          const { data: aiResponse, error } = await supabase.functions.invoke('parse-job-description', {
            body: { jobDescriptionContent: content },
            headers: { Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` }
          });

          if (error) throw error;
          setParsedJobDescription(aiResponse.parsedJobDescription);
          
          // Close the window
          if (newWindow && !newWindow.closed) {
            newWindow.close();
          }
        } catch (error) {
          logger.error('Error parsing job description:', error);
          setJobUrlError('Failed to parse job description. Please copy and paste it manually.');
        } finally {
          setNewWindow(null);
          setIsParsing(false);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [newWindow, isAuthenticated, authUser]);

  /**
   * Fetches and processes resume files from Supabase
   * Uses ModernFileService for centralized file management
   * Handles SSR data if available
   */
  const fetchResumes = async () => {
    if (!user) return;
    
    // If we already have resumes from SSR and this is the initial load, skip fetching
    if (initialResumes.length > 0 && resumes.length === initialResumes.length) {
      logger.debug('Using SSR resume data, skipping fetch');
      return;
    }

    try {
      setIsLoadingResumes(true);
      
      // Track data loading
      trackFeatureUsage('resumes_fetch', 'started', {
        journey_id: journeyIdRef.current
      });
      
      const startTime = performance.now();
      const fileService = ModernFileService.getInstance();
      const files = await fileService.getResumes(user.id);
      setResumes(files);
      
      // Track successful data loading
      trackFeatureUsage('resumes_fetch', 'completed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        count: files.length
      });
      
      // Track performance metrics
      trackFeatureUsage('data_fetch_performance', 'measured', {
        route: '/resumes',
        operation: 'fetch_resumes',
        load_time_ms: Math.round(performance.now() - startTime),
        first_paint_ms: Math.round(
          performance.getEntriesByType('paint')
            .find(entry => entry.name === 'first-paint')?.startTime || performance.now()
        )
      });
    } catch (error: unknown) {
      logger.error('Error fetching resumes:', error);
      showToast('Failed to load resumes', 'error');
      
      // Track error
      trackFeatureUsage('resumes_fetch', 'failed', {
        journey_id: journeyIdRef.current,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        step: 'loading_resumes'
      });
    } finally {
      setIsLoadingResumes(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const startTime = performance.now();
    setIsUploading(true);
    
    // Start upload journey
    const journeyId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setUploadJourneyId(journeyId);
    
    // Track upload initiation
    trackFeatureUsage('resume_upload', 'initiated', {
      journey_id: journeyId,
      file_type: file.type,
      file_size: file.size
    });
    
    trackUserJourney('resume_upload_started', {
      journey_id: journeyId,
      file_type: file.type,
      file_size: file.size
    });
    
    try {
      const fileService = ModernFileService.getInstance();
      const uploadedFile = await fileService.uploadFile(file, {
        usage: {
          type: 'resume',
          context: 'resume_upload'
        }
      });
      
      // Track successful upload
      trackFeatureUsage('resume_upload', 'completed', {
        journey_id: journeyId,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        file_type: file.type,
        file_size: file.size
      });
      
      trackUserJourney('resume_upload_completed', {
        journey_id: journeyId,
        success: true,
        duration_ms: Math.round(performance.now() - startTime)
      });
      
      setUploadJourneyId(null);
      
      // Refresh files
      fetchResumes();
      showToast('Resume uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading resume:', error);
      showToast('Failed to upload resume', 'error');
      
      // Track error
      trackFeatureUsage('resume_upload', 'failed', {
        journey_id: journeyId,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        file_type: file.type,
        file_size: file.size
      });
      
      trackUserJourney('resume_upload_failed', {
        journey_id: journeyId,
        failure_reason: 'upload_error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        duration_ms: Math.round(performance.now() - startTime)
      });
      
      setUploadJourneyId(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  /**
   * Handles the deletion of resumes
   * Removes file from storage and updates database
   */
  const handleDeleteResume = async (resume: Resume) => {
    const startTime = performance.now();
    
    // Track delete action
    trackFeatureUsage('resume_delete', 'initiated', {
      journey_id: journeyIdRef.current,
      resume_id: resume.id
    });
    
    if (!user) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this resume?');
    if (!confirmDelete) return;

    try {
      const fileService = ModernFileService.getInstance();
      await fileService.deleteFile(resume);
      fetchResumes();
      showToast('Resume deleted successfully', 'success');
      
      // Track successful deletion
      trackFeatureUsage('resume_delete', 'completed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: true,
        resume_id: resume.id
      });
    } catch (error) {
      console.error('Error deleting resume:', error);
      showToast('Failed to delete resume', 'error');
      
      // Track error
      trackFeatureUsage('resume_delete', 'failed', {
        journey_id: journeyIdRef.current,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        resume_id: resume.id
      });
    }
  };

  /**
   * Manages resume viewing functionality
   * Handles different file sources (local vs Google Drive)
   */
  const handleViewResume = async (resume: Resume) => {
    // Track view action
    trackFeatureUsage('resume_view', 'initiated', {
      journey_id: journeyIdRef.current,
      resume_id: resume.id,
      source: resume.source
    });
    
    try {
      const fileService = ModernFileService.getInstance();
      // Use getFileUrl instead of getSignedUrl
      const url = await fileService.getFileUrl(resume);
      
      // For Google Drive files, open in new tab
      if (resume.source === 'google_drive') {
        window.open(url, '_blank');
        return;
      }
      
      if (!url) {
        throw new Error('Failed to get file URL');
      }
      
      // Set the URL and open the modal
      setSelectedResume(resume);
      setSelectedResumeUrl(url);
      setIsResumeModalOpen(true);
      
      // Track successful view
      trackFeatureUsage('resume_view', 'completed', {
        journey_id: journeyIdRef.current,
        success: true,
        resume_id: resume.id,
        source: resume.source
      });
    } catch (error) {
      logger.error('Error viewing resume:', error);
      showToast('Failed to view resume', 'error');
      
      // Track error
      trackFeatureUsage('resume_view', 'failed', {
        journey_id: journeyIdRef.current,
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        resume_id: resume.id,
        source: resume.source
      });
    }
  };

  /**
   * Handles cleaning of HTML content from job posting websites
   * Supports various job board formats (Workday, Greenhouse, Lever)
   */
  const cleanHtml = (html: string): string => {
    logger.debug('Starting HTML cleaning process');
    logger.debug(`Input HTML length: ${html.length} characters`);

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    logger.debug(`Created temp div with ${tempDiv.children.length} child elements`);

    // Remove script and style elements
    const scripts = tempDiv.getElementsByTagName('script');
    const styles = tempDiv.getElementsByTagName('style');
    logger.debug(`Found ${scripts.length} script tags and ${styles.length} style tags to remove`);
    while (scripts.length > 0) scripts[0].remove();
    while (styles.length > 0) styles[0].remove();
    logger.debug('Removed all script and style tags');

    // Define site-specific selectors
    const siteSpecificSelectors = {
      workday: [
        // Primary Workday selectors
        '[data-automation-id="jobPostingHeader"]',
        '[data-automation-id="jobPostingDescription"]',
        '[data-automation-id="jobPostingSection"]',
        // Workday section selectors
        '[data-automation-id="job_posting_title"]',
        '[data-automation-id="formField"]',
        '[data-automation-id="jobPostingDescription"]',
        // Workday container selectors
        'div[class*="css-"] > div[class*="css-"]', // Workday uses CSS modules
        // Additional Workday patterns
        '[class*="jobPostingSection"]',
        '[class*="job-description"]',
        // Fallback selectors
        'main',
        '#mainContent',
        '.WFMWO2'  // Common Workday class
      ],
      greenhouse: [
        '#content',
        '#app-body',
        '#job_content',
        '.job-content'
      ],
      lever: [
        '.posting-headline',
        '.posting-description',
        '.content'
      ]
    };

    // Try to find job description container using site-specific selectors first
    const urlDomain = jobDescriptionUrl.toLowerCase();
    let siteSpecificContent = '';
    
    if (urlDomain.includes('myworkdayjobs.com') || urlDomain.includes('workday')) {
      logger.debug('Detected Workday site, using Workday-specific selectors');
      
      // First try to get all form fields which usually contain the job details
      const formFields = tempDiv.querySelectorAll('[data-automation-id="formField"]');
      if (formFields.length > 0) {
        logger.debug(`Found ${formFields.length} form fields`);
        const texts = Array.from(formFields).map(field => field.textContent || '').filter(text => text.length > 0);
        if (texts.length > 0) {
          siteSpecificContent = texts.join('\n\n');
          logger.debug(`Extracted content from ${texts.length} form fields`);
        }
      }

      // If form fields didn't work, try other Workday selectors
      if (!siteSpecificContent) {
        for (const selector of siteSpecificSelectors.workday) {
          const elements = tempDiv.querySelectorAll(selector);
          logger.debug(`Trying selector "${selector}": found ${elements.length} elements`);
          
          elements.forEach(el => {
            const text = el.textContent || '';
            logger.debug(`Element text length: ${text.length}`);
            if (text.length > siteSpecificContent.length) {
              siteSpecificContent = text;
              logger.debug(`Found better content with selector "${selector}"`);
            }
          });
        }
      }
    } else if (urlDomain.includes('greenhouse.io')) {
      logger.debug('Detected Greenhouse site, using Greenhouse-specific selectors');
      for (const selector of siteSpecificSelectors.greenhouse) {
        const element = tempDiv.querySelector(selector);
        if (element?.textContent) {
          siteSpecificContent = element.textContent;
          break;
        }
      }
    } else if (urlDomain.includes('lever.co')) {
      logger.debug('Detected Lever site, using Lever-specific selectors');
      for (const selector of siteSpecificSelectors.lever) {
        const element = tempDiv.querySelector(selector);
        if (element?.textContent) {
          siteSpecificContent = element.textContent;
          break;
        }
      }
    }

    if (siteSpecificContent) {
      logger.debug('Found content using site-specific selectors');
      logger.debug('Site-specific content length:', { length: siteSpecificContent.length });
      logger.debug('Sample of site-specific content:', { sample: siteSpecificContent.substring(0, 200) });
      return siteSpecificContent.replace(/\s+/g, ' ').trim();
    }

    // If site-specific selectors didn't work, try generic selectors
    logger.debug('No site-specific content found, trying generic selectors');
    const possibleContainers = [
      ...(Array.from(tempDiv.querySelectorAll('[class*="job-description"], [class*="jobDescription"], [class*="description"], [id*="job-description"], [id*="jobDescription"], [class*="posting"], [class*="details"], main, article')) as Element[]),
      ...(Array.from(tempDiv.querySelectorAll('[role="main"]')) as Element[]),
      ...(Array.from(tempDiv.querySelectorAll('div[class*="css-"]')) as Element[]),
      ...(Array.from(tempDiv.querySelectorAll('div > div > div')) as Element[])
    ];

    logger.debug(`Found ${possibleContainers.length} possible content containers`);
    
    // Find the container with the most text content
    let bestContainer: Element | null = null;
    let maxLength = 0;
    
    possibleContainers.forEach((container, index) => {
      const text = (container as HTMLElement).textContent || '';
      logger.debug(`Container ${index} length: ${text.length}`);
      if (text.length > maxLength && text.length < 50000) {
        maxLength = text.length;
        bestContainer = container as Element;
        logger.debug(`Found new best container with length ${text.length}`);
      }
    });

    let text = '';
    if (bestContainer) {
      logger.debug('Using best container found');
      text = (bestContainer as HTMLElement).textContent || '';
      logger.debug('Best container text length:', { length: text.length });
      logger.debug('Sample of best container text:', { sample: text.substring(0, 200) });
    } else {
      logger.debug('No suitable container found, using full body content');
      text = tempDiv.textContent || '';
      logger.debug('Full body text length:', { length: text.length });
    }

    // Clean up the text
    text = text
      .replace(/\s+/g, ' ')  // Normalize whitespace
      .replace(/\n+/g, '\n') // Normalize newlines
      .replace(/[^\S\n]+/g, ' ') // Replace multiple spaces with single space, preserve newlines
      .trim();

    logger.debug(`Final cleaned text length: ${text.length} characters`);
    logger.debug('Final cleaned text length:', { length: text.length, unit: 'characters' });
    if (text.length > 0) {
      logger.debug('Sample of final text:', { sample: text.substring(0, 200) });
    } else {
      logger.debug('Warning: No text content found after cleaning');
    }

    return text;
  };

  /**
   * Handles the parsing of job description URLs
   * Attempts to extract job description content from various job board formats
   */
  const handleParseJobUrl = async () => {
    if (!jobDescriptionUrl) {
      alert('Please enter a job description URL');
      return;
    }

    logger.debug(`Starting job URL parsing for: ${jobDescriptionUrl}`);
    setIsParsing(true);
    setJobUrlError(null);
    setParsingStatus('Initializing...');

    try {
      // First try to fetch directly using a CORS proxy
      logger.debug('Attempting to fetch via CORS proxy');
      setParsingStatus('Fetching job posting content...');
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(jobDescriptionUrl)}`;
      
      const response = await fetch(proxyUrl);
      const data = await response.json();
      
      if (data.contents) {
        logger.debug('Successfully fetched content via proxy');
        setParsingStatus('Cleaning and processing content...');
        const cleanedContent = cleanHtml(data.contents);
        
        if (cleanedContent && cleanedContent.length > 100) {
          logger.debug('Successfully cleaned content, length:', { length: cleanedContent.length });
          setParsingStatus('Analyzing job description with AI...');
          
          // Use OpenAI to parse the content
          if (!isAuthenticated || !authUser) throw new Error('User not authenticated');
          
          // Keep the authorization header
          const { data: aiResponse, error } = await supabase.functions.invoke('parse-job-description', {
            body: { jobDescriptionContent: cleanedContent },
            headers: { Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` }
          });

          if (error) throw error;
          setParsedJobDescription(aiResponse.parsedJobDescription);
          setParsingStatus('');
          return;
        }
        
        logger.debug('Cleaned content too short or empty, falling back to manual instructions');
      }

      // If we couldn't parse directly, show instructions
      logger.debug('Direct parsing failed, showing manual instructions');
      setParsingStatus('Preparing manual entry instructions...');
      const instructionsHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Job Description Instructions</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              line-height: 1.5;
              max-width: 800px;
              margin: 20px auto;
              padding: 20px;
            }
            .instructions {
              background: #f0f9ff;
              border: 1px solid #bae6fd;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 20px;
            }
            .button {
              background: #3b82f6;
              color: white;
              border: none;
              padding: 8px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
            }
            .button:hover {
              background: #2563eb;
            }
            .steps {
              margin-left: 20px;
            }
            .note {
              font-style: italic;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="instructions">
            <h2>How to Copy the Job Description</h2>
            <ol class="steps">
              <li>The job posting will open in a new tab</li>
              <li>Wait for the page to fully load</li>
              <li>Select and copy (Ctrl/Cmd + C) the entire job description</li>
              <li>Return to the previous tab</li>
              <li>Paste the content in the text area</li>
            </ol>
            <p class="note">Note: This manual process ensures we get the most accurate content from the job posting.</p>
          </div>
          <button class="button" onclick="openJobPage()">Open Job Posting</button>
          <script>
            function openJobPage() {
              window.location.href = "${jobDescriptionUrl}";
            }
          </script>
        </body>
        </html>
      `;

      // Create a blob URL for the instructions
      const blob = new Blob([instructionsHtml], { type: 'text/html' });
      const instructionsUrl = URL.createObjectURL(blob);

      // Open the instructions in a new tab
      window.open(instructionsUrl, '_blank');

      // Switch to text input mode
      setJobDescriptionType('text');
      setJobUrlError('Please follow the instructions in the new tab to copy and paste the job description.');
      
      // Clean up the blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(instructionsUrl);
      }, 1000);

    } catch (error) {
      logger.error('Error parsing job URL:', error);
      setJobUrlError('Failed to parse job posting. Please try copying and pasting the job description manually.');
    } finally {
      setIsParsing(false);
      setParsingStatus('');
    }
  };

  /**
   * IMPORTANT: User Flow for Resume Suggestions
   * 1. User clicks "Get Suggestions" button
   * 2. Filter modal opens to let user select relevant notes and career cards
   * 3. Only after user selects and submits in the filter modal should we proceed to generate suggestions
   * 
   * This two-step flow is intentional to ensure users can provide additional context
   * through their notes and career cards before generating suggestions.
   * DO NOT modify this flow to skip the filter modal step.
   */

  const handleGetSuggestions = async () => {
    const startTime = performance.now();
    
    // Start tailoring journey
    const journeyId = `tailoring_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setTailoringJourneyId(journeyId);
    
    // Track tailoring initiation
    trackFeatureUsage('resume_tailoring', 'initiated', {
      journey_id: journeyId,
      resume_id: selectedResumeForSuggestions?.id,
      job_description_type: jobDescriptionType
    });
    
    trackUserJourney('resume_tailoring_started', {
      journey_id: journeyId,
      resume_id: selectedResumeForSuggestions?.id,
      job_description_type: jobDescriptionType
    });
    
    try {
      if (!selectedResumeForSuggestions) return;
      
      // Check if data is still loading
      if (notesLoading || careerCardsLoading) {
        showToast('Loading your notes and career cards...', 'info');
        return;
      }
      
      setIsFilterModalOpen(true);  // Step 1: Open filter modal first
      
      // Track successful tailoring initiation
      trackFeatureUsage('resume_tailoring', 'filter_modal_opened', {
        journey_id: journeyId,
        duration_ms: Math.round(performance.now() - startTime),
        success: true
      });
      
      trackUserJourney('resume_tailoring_filter_opened', {
        journey_id: journeyId,
        success: true,
        duration_ms: Math.round(performance.now() - startTime)
      });
      
      setTailoringJourneyId(null);
    } catch (error) {
      logger.error('Error getting suggestions:', error);
      showToast('Failed to get suggestions', 'error');
      
      // Track error
      trackFeatureUsage('resume_tailoring', 'failed', {
        journey_id: journeyId,
        duration_ms: Math.round(performance.now() - startTime),
        success: false,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        step: 'tailoring_processing'
      });
      
      trackUserJourney('resume_tailoring_failed', {
        journey_id: journeyId,
        failure_reason: 'tailoring_error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        duration_ms: Math.round(performance.now() - startTime)
      });
      
      setTailoringJourneyId(null);
    }
  };

  /**
   * Called only after user submits the filter modal.
   * This function handles the actual suggestion generation using:
   * - The selected resume
   * - The job description
   * - User-selected notes and career cards from the filter modal
   * 
   * @important This should never be called directly from the "Get Suggestions" button click.
   * It should only be triggered after user interaction with the filter modal.
   */
  const handleFilterModalSubmit = async () => {
    setIsFilterModalOpen(false);
    setIsGeneratingSuggestions(true);
    setSuggestionsStatus('Analyzing resume and job description...');

    try {
      if (!selectedResumeForSuggestions?.file_path || 
          !selectedResumeForSuggestions?.source || 
          selectedResumeForSuggestions?.external_download_url === undefined) {
        throw new Error('Invalid resume data');
      }

      if (!isAuthenticated || !authUser) throw new Error('User not authenticated');

      // Get the selected career cards data
      const selectedCardsData = careerCards
        .filter(card => selectedCareerCards.includes(card.cardid))
        .map(card => ({
          id: card.cardid,
          data: card.card_data
        }));

      // Get the selected notes data
      const selectedNotesData = notes
        .filter(note => selectedNotes.includes(note.id));

      // Keep the authorization header
      const { data, error } = await supabase.functions.invoke('tailor-resume', {
        body: {
          resumePath: selectedResumeForSuggestions.file_path,
          source: selectedResumeForSuggestions.source,
          externalUrl: selectedResumeForSuggestions.external_download_url,
          jobDescription: jobDescriptionType === 'text' ? jobDescriptionText : parsedJobDescription,
          selectedNotes: selectedNotesData,
          selectedCareerCards: selectedCardsData
        },
        headers: { Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` }
      });

      if (error) throw error;

      logger.debug('Suggestions data:', data);

      setSuggestionsStatus('Formatting suggestions...');
      setSuggestions(data.suggestions);
      setSuggestionsStatus('');
    } catch (error) {
      logger.error('Error getting suggestions:', error);
      showToast('Failed to get suggestions', 'error');
    } finally {
      setIsGeneratingSuggestions(false);
      setSuggestionsStatus('');
    }
  };

  /**
   * Generates signed URLs for resume file access
   * Uses ModernFileService for centralized file management
   */
  const getSignedUrl = async (resume: Resume) => {
    try {
      const fileService = ModernFileService.getInstance();
      return await fileService.getFileUrl(resume);
    } catch (error) {
      logger.error('Error generating signed URL:', error);
      return null;
    }
  };

  useEffect(() => {
    if (selectedResumeForSuggestions && suggestions) {
      getSignedUrl(selectedResumeForSuggestions).then(url => {
        setSignedResumeUrl(url);
      });
    }
  }, [selectedResumeForSuggestions, suggestions]);

  // Function to highlight area on resume
  const highlightResumeArea = (area: string, originalText?: string) => {
    // Get the iframe container
    const iframeContainer = iframeRef.current?.parentElement;
    if (!iframeContainer) return;

    // Remove any existing highlights
    const existingHighlight = iframeContainer.querySelector('.suggestion-highlight');
    if (existingHighlight) {
      existingHighlight.remove();
    }

    // Create highlight overlay
    const highlight = document.createElement('div');
    highlight.className = 'suggestion-highlight';
    highlight.style.cssText = `
      position: absolute;
      background: rgba(59, 130, 246, 0.2);
      border: 2px solid rgb(59, 130, 246);
      pointer-events: none;
      z-index: 1000;
      transition: all 0.3s ease;
    `;

    // If we have original text, try to find its position in the resume
    if (originalText && iframeRef.current) {
      const iframe = iframeRef.current;
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          // Create a text finder
          const range = iframeDoc.createRange();
          const textNodes: Node[] = [];
          
          // Function to get all text nodes
          const getTextNodes = (node: Node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              textNodes.push(node);
            } else {
              node.childNodes.forEach(child => getTextNodes(child));
            }
          };

          getTextNodes(iframeDoc.body);

          // Find the text position
          let found = false;
          for (const node of textNodes) {
            const text = node.textContent || '';
            if (text.includes(originalText)) {
              range.selectNode(node);
              const rect = range.getBoundingClientRect();
              
              // Convert coordinates relative to iframe
              const iframeRect = iframe.getBoundingClientRect();
              highlight.style.top = `${rect.top - iframeRect.top}px`;
              highlight.style.left = `${rect.left - iframeRect.left}px`;
              highlight.style.width = `${rect.width}px`;
              highlight.style.height = `${rect.height}px`;
              found = true;
              break;
            }
          }

          // If exact text not found, fallback to section highlighting
          if (!found) {
            setDefaultHighlight(highlight, area);
          }
        }
      } catch (error) {
        // Fallback to section highlighting if there's an error
        setDefaultHighlight(highlight, area);
      }
    } else {
      // Use default section highlighting if no original text provided
      setDefaultHighlight(highlight, area);
    }

    iframeContainer.appendChild(highlight);
  };

  // Helper function for default section highlighting
  const setDefaultHighlight = (highlight: HTMLElement, area: string) => {
    // More precise section mappings
    switch (area.toLowerCase()) {
      case 'skills':
        highlight.style.top = '75%';
        highlight.style.height = '10%';
        break;
      case 'experience':
        highlight.style.top = '15%';
        highlight.style.height = '50%';
        break;
      case 'education':
        highlight.style.top = '65%';
        highlight.style.height = '10%';
        break;
      default:
        highlight.style.top = '0';
        highlight.style.height = '100%';
    }
    highlight.style.left = '0';
    highlight.style.width = '100%';
  };

  // Helper function to convert to title case
  const toTitleCase = (str: string) => {
    return str.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  /**
   * Text highlighting functionality
   * Manages the highlighting of relevant text in the resume when clicking suggestions
   */
  const highlightText = (searchText: string) => {
    const textLayer = textLayerRef.current;
    if (!textLayer) {
      logger.debug('Text layer not found');
      return false;
    }

    // Clear existing highlights
    clearHighlights();

    const cleanedSearchText = cleanTextForMatching(searchText);
    logger.debug('Attempting to highlight text:', { 
      original: searchText,
      cleaned: cleanedSearchText 
    });

    let found = false;
    const textElements = Array.from(textLayer.querySelectorAll('span[data-text]'));
    
    logger.debug(`Found ${textElements.length} text elements to search through`);

    // Create a pattern that matches the whole phrase with flexible spacing
    const pattern = new RegExp(
      cleanedSearchText
        .split(' ')
        .map(word => `(?:${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`)
        .join('[\\s\\(\\)\\[\\]\\{\\}]*'),
      'i'
    );

    logger.debug('Search pattern:', { pattern: pattern.toString() });

    // Search through text elements
    textElements.forEach((element, index) => {
      const elementText = cleanTextForMatching(element.getAttribute('data-text') || '');
      
      if (pattern.test(elementText)) {
        found = true;
        logger.debug('Found match:', {
          elementIndex: index,
          elementText: elementText
        });
        createHighlight(element as HTMLElement, textLayer);
      }
    });

    logger.debug('Search completed:', {
      searchText: cleanedSearchText,
      found,
      highlightsCreated: textLayer.querySelectorAll('.suggestion-highlight').length
    });

    return found;
  };

  // Helper function to create regex pattern from text
  const createSearchPattern = (text: string): RegExp => {
    const cleanedText = cleanTextForMatching(text)
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => {
        // Escape regex special chars
        const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Make each word optional to handle variations
        return `(?:${escaped})?`;
      })
      .join('\\s*'); // Allow any amount of whitespace between words
    return new RegExp(cleanedText, 'i');
  };

  /**
   * Helper function to create highlights for text matches
   * Creates a visual overlay on top of matched text in the resume
   */
  const createHighlight = (element: HTMLElement, textLayer: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const layerRect = textLayer.getBoundingClientRect();

    const highlight = document.createElement('div');
    highlight.className = 'suggestion-highlight';
    
    // Position highlight exactly over the text element
    highlight.style.cssText = `
      position: absolute;
      background: rgba(59, 130, 246, 0.2);
      border: 2px solid rgb(59, 130, 246);
      pointer-events: none;
      z-index: 1;
      top: ${rect.top - layerRect.top}px;
      left: ${rect.left - layerRect.left}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      transition: all 0.3s ease;
    `;

    textLayer.appendChild(highlight);
  };

  /**
   * Removes all highlight overlays from the resume
   * Called before applying new highlights or when clearing the UI
   */
  const clearHighlights = () => {
    const textLayer = textLayerRef.current;
    if (!textLayer) return;

    const highlights = textLayer.querySelectorAll('.suggestion-highlight');
    highlights.forEach(highlight => {
      if (highlight instanceof HTMLElement) {
        highlight.style.opacity = '0';
        setTimeout(() => highlight.remove(), 300);
      } else {
        highlight.remove();
      }
    });
  };

  /**
   * PDF rendering and highlighting functionality
   * Handles the display of PDF resumes and interactive highlighting
   */
  const renderPdfViewer = () => {
    if (!selectedResumeForSuggestions || !signedResumeUrl) {
      return null;
    }

    return (
      <div className="relative w-full h-full">
        <canvas
          ref={canvasRef}
          className="w-full h-auto"
        />
        <div
          ref={textLayerRef}
          className="absolute top-0 left-0 w-full h-full"
          style={{
            color: 'rgba(255, 0, 0, 0.0)',
            fontSize: '12px',
            fontFamily: 'monospace',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 2
          }}
        />
      </div>
    );
  };

  // Update the PDF loading effect
  useEffect(() => {
    let currentPage: PDFPageProxy | null = null;
    let renderTask: RenderTask | null = null;
    let loadingTask: PDFDocumentLoadingTask | null = null;
    let isCurrentEffect = true; // Flag to track if this effect is still current

    const cleanupResources = () => {
      // Cancel any ongoing render task
      if (renderTask) {
        renderTask.cancel();
        renderTask = null;
      }

      // Clean up the current page
      if (currentPage) {
        currentPage.cleanup();
        currentPage = null;
      }

      // Cancel any ongoing loading task
      if (loadingTask) {
        loadingTask.destroy();
        loadingTask = null;
      }

      // Clear canvas and text layer
      if (canvasRef.current) {
        const context = canvasRef.current.getContext('2d');
        context?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      if (textLayerRef.current) {
        textLayerRef.current.innerHTML = '';
      }

      // Reset PDF loaded state
      if (isCurrentEffect) {
        setIsPdfLoaded(false);
      }
    };

    const loadPdf = async (pdfUrl: string) => {
      if (!canvasRef.current || !textLayerRef.current || !isCurrentEffect) {
        return;
      }

      try {
        cleanupResources(); // Clean up before starting new render

        const canvas = canvasRef.current;
        const textLayer = textLayerRef.current;
        const context = canvas.getContext('2d');
        
        if (!context) {
          logger.error('Could not get canvas context');
          return;
        }

        // Use a blob URL approach to avoid CSP issues
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        
        const pdfBlob = await response.blob();
        const blobUrl = URL.createObjectURL(pdfBlob);
        
        loadingTask = pdfjsLib.getDocument({
          url: blobUrl,
          // Use relative paths that match our public directory structure
          cMapUrl: '/cmaps/',
          cMapPacked: true,
          standardFontDataUrl: '/standard_fonts/'
        });
        
        const pdf = await loadingTask.promise;
        
        if (!isCurrentEffect) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        currentPage = await pdf.getPage(1);
        
        if (!isCurrentEffect) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        // Calculate scale to fit container width
        const parent = canvas.parentElement;
        const containerWidth = parent?.clientWidth || 800;
        const scale = containerWidth / currentPage.getViewport({ scale: 1.0 }).width;
        const viewport = currentPage.getViewport({ scale });

        // Set canvas and text layer dimensions
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        textLayer.style.height = `${viewport.height}px`;
        textLayer.style.width = `${viewport.width}px`;

        // Position text layer absolutely over canvas
        textLayer.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: ${viewport.width}px;
          height: ${viewport.height}px;
          transform-origin: left bottom;
          mix-blend-mode: multiply;
          pointer-events: none;
          user-select: none;
          z-index: 2;
        `;
        
        // Render PDF page
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        renderTask = currentPage.render(renderContext);
        await renderTask.promise;

        if (!isCurrentEffect) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        // Get text content with exact positioning
        const textContent = await currentPage.getTextContent();
        textLayer.innerHTML = '';

        // Process each text item
        textContent.items.forEach((item: TextItem | TextMarkedContent) => {
          if (!isTextItem(item)) return;

          const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
          
          // Get font size from the transform matrix
          const fontHeight = Math.sqrt((tx[0] * tx[0]) + (tx[1] * tx[1]));
          
          const textElement = document.createElement('span');
          textElement.textContent = item.str;
          textElement.dataset.text = item.str;
          
          // Get font properties from the item
          const fontFamily = 'fontName' in item ? item.fontName || 'sans-serif' : 'sans-serif';
          const fontSize = `${fontHeight}px`;
          
          // Use PDF.js's font weight and style information
          let fontWeight = 'normal';
          let fontStyle = 'normal';

          // Check actual font weight from PDF.js
          if ('fontWeight' in item) {
            fontWeight = Number(item.fontWeight) >= 600 ? 'bold' : String(item.fontWeight);
          }

          // Check for italic style
          if (fontFamily && /italic|oblique/i.test(fontFamily)) {
            fontStyle = 'italic';
          }
          
          textElement.style.cssText = `
            position: absolute;
            left: ${tx[4]}px;
            top: ${tx[5] - (fontHeight)}px;
            font-size: ${fontSize};
            font-family: ${fontFamily};
            font-weight: ${fontWeight};
            font-style: ${fontStyle};
            transform-origin: left bottom;
            transform: matrix(${tx[0] / fontHeight}, ${tx[1] / fontHeight},
                     ${tx[2] / fontHeight}, ${-tx[3] / fontHeight},
                     0, 0);
            white-space: pre;
            color: transparent;
            z-index: 2;
            pointer-events: none;
            user-select: none;
          `;

          textLayer.appendChild(textElement);
        });

        setIsPdfLoaded(true);
        
        // Clean up blob URL when done
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        if (isCurrentEffect) {
          logger.error('Error loading PDF:', error);
          throw error;
        }
      }
    };

    if (signedResumeUrl) {
      loadPdf(signedResumeUrl).catch(error => {
        if (isCurrentEffect) {
          logger.error('Error in PDF loading effect:', error);
        }
      });
    }

    // Cleanup function
    return () => {
      isCurrentEffect = false;
      cleanupResources();
    };
  }, [signedResumeUrl]);

  /**
   * Limits text length for display purposes
   * Ensures consistent and readable suggestion displays
   */
  const limitWords = (text: string): string => {
    return text.split(/\s+/).slice(0, 15).join(' ');
  };

  // Update the extractText function in handleSuggestionClick
  const handleSuggestionClick = (area: string, highlights: any[]) => {
    setHighlightedArea(area);
    clearHighlights();
    
    // Helper function to extract text from a highlight object or string
    const extractText = (highlight: any): string => {
      if (!highlight) return '';
      if (typeof highlight === 'string') return limitWords(highlight);
      if (typeof highlight === 'object' && highlight !== null) {
        if ('text' in highlight) return limitWords(highlight.text);
        if ('name' in highlight) return limitWords(highlight.name);
        if ('description' in highlight) return limitWords(highlight.description);
      }
      return '';
    };
    
    // Process highlights to get valid text strings
    const validHighlights = highlights
      .map(extractText)
      .filter(text => text && text.trim() !== '');

    if (!validHighlights.length) {
      return;
    }

    // Try to highlight each text
    validHighlights.forEach(text => {
      if (text && text.length > 2) {
        highlightText(text);
      }
    });
  };

  // Update the SuggestionItem component to be clickable
  interface SuggestionItemProps {
    area: string;
    children: string;
    highlights: string[];
    onHighlight: (
      highlights: string[], 
      positions?: Array<{
        text: string, 
        positions: Array<{
          element: HTMLElement, 
          text: string
        }>
      }>
    ) => void;
  }

  const SuggestionsContent = () => {
    const processedContent = useMemo(() => {
      return processMarkdown(suggestions);
    }, [suggestions]);
    
    return (
      <div className="space-y-2">
        <div className="mb-2">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Click on suggestions to highlight relevant areas in your resume
          </div>
        </div>
        <div className="space-y-4">
          {processedContent}
        </div>
      </div>
    );
  };

  // Update the markdown components configuration
  const markdownComponents: Components = {
    p: ({ children, ...props }) => (
      <p className="my-0.5" {...props}>{children}</p>
    ),
    ul: ({ children, ...props }) => (
      <ul className="my-0.5 list-disc list-inside" {...props}>{children}</ul>
    ),
    li: ({ children, ...props }) => (
      <li className="my-0" {...props}>{children}</li>
    ),
    strong: ({ children, ...props }) => (
      <strong className="font-semibold" {...props}>{children}</strong>
    ),
    h1: ({ children, ...props }) => (
      <h1 className="text-lg font-bold my-1" {...props}>{children}</h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className="text-base font-semibold my-1" {...props}>{children}</h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className="text-sm font-semibold my-1" {...props}>{children}</h3>
    ),
  };

  // Update the processMarkdown function to handle objects in highlights
  const processMarkdown = (markdown: string) => {
    // Helper function to clean up content
    const cleanContent = (content: string) => {
      return content
        .trim()
        .replace(/\r\n/g, '\n')
        .replace(/^\s*[•-]\s*$\n?/gm, '')
        .replace(/^\s*[•-]\s*\n+#{1,6}\s/gm, '#')
        .replace(/^\s*[•-]\s*\n+\{\{area/gm, '{{area')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/^[-•]\s*([^\n]+)/gm, '* $1')
        .replace(/\n\s+([^#\n][^\n]*)/g, ' $1')
        .replace(/\n+#{1,6}/g, '\n\n#')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    };

    // Split content by area and highlight tags
    const parts = markdown.split(/(\{\{(?:\/)?(?:area|highlight):?[^}]*\}\})/);

    let result: (string | JSX.Element)[] = [];
    let currentArea: string | null = null;
    let currentHighlights: string[] = [];
    let currentContent = '';
    let isInArea = false;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i] || '';
      if (!part.trim()) continue;

      if (part.startsWith('{{area:')) {
        currentArea = part.match(/\{\{area:([^}]*)\}\}/)?.[1]?.trim() || 'general';
        isInArea = true;
        currentContent = '';
        currentHighlights = [];
      } else if (part.startsWith('{{/area}}')) {
        if (isInArea && currentArea) {
          const cleanedContent = cleanContent(currentContent);
          if (cleanedContent) {
            try {
              const validHighlights = currentHighlights
              .filter(h => h && h !== 'undefined' && h.trim() !== '')
              .map(h => cleanTextForMatching(h));

              result.push(
                <SuggestionItem 
                  key={`area-${result.length}`}
                  area={currentArea}
                  highlights={validHighlights}
                  onHighlight={handleHighlight}
                >
                  {cleanedContent}
                </SuggestionItem>
              );
            } catch (error) {
              logger.error('Error creating SuggestionItem:', error);
            }
          }
        }
        isInArea = false;
        currentArea = null;
        currentHighlights = [];
        currentContent = '';
      } else if (part.startsWith('{{highlight:')) {
        try {
          const highlightMatch = part.match(/\{\{highlight:([^}]*)\}\}/);
          if (highlightMatch) {
            const highlightData = highlightMatch[1].trim();
            try {
              const parsedHighlights = JSON.parse(highlightData);
              currentHighlights = Array.isArray(parsedHighlights) 
                ? parsedHighlights
                    .map(h => {
                      if (h === '[object Object]' || Object.prototype.toString.call(h) === '[object Object]') {
                        return '';
                      }
                      if (h === null || h === undefined) return '';
                      if (typeof h === 'string') return h;
                      if (typeof h === 'object') {
                        if ('text' in h) return h.text;
                        if ('name' in h) return h.name;
                        if ('description' in h) return h.description;
                        const stringValue = Object.values(h).find(v => typeof v === 'string');
                        if (stringValue) return stringValue;
                        return '';
                      }
                      return String(h);
                    })
                    .filter(h => 
                      h && 
                      h.trim() !== '' && 
                      h !== 'undefined' && 
                      h !== '[object Object]' &&
                      !h.includes('[object Object]')
                    )
                    .map(h => h.trim())
                : [];
            } catch (parseError) {
              logger.error('Failed to parse highlights:', { error: parseError, data: highlightData });
              currentHighlights = [];
            }
          }
        } catch (error) {
          logger.error('Error processing highlight tag:', error);
          currentHighlights = [];
        }
      } else {
        if (isInArea) {
          currentContent += part;
        } else {
          const cleanedContent = cleanContent(part);
          if (cleanedContent) {
            result.push(
              <ReactMarkdown 
                key={`md-${result.length}`}
                components={markdownComponents}
                skipHtml={true}
                remarkPlugins={[]}
                allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3']}
              >
                {cleanedContent}
              </ReactMarkdown>
            );
          }
        }
      }
    }

    return result;
  };

  // Update the handleHighlight function
  const handleHighlight = (highlights: (string | HighlightText)[], area: string | null) => {
    const textLayer = textLayerRef.current;
    if (!textLayer) {
      setHighlightedArea(null);
      return;
    }

    // Clear existing highlights
    const existingHighlights = textLayer.querySelectorAll('.highlight-text');
    existingHighlights.forEach(el => el.remove());

    if (!highlights.length || !area) {
      setHighlightedArea(null);
      return;
    }

    // Add new highlights
    highlights.forEach(highlight => {
      const text = typeof highlight === 'string' ? highlight : highlight.text || '';
      if (text && text.length >= 2) {
        const elements = findTextInElement(textLayer, text);
        elements.forEach(element => {
          const highlightEl = document.createElement('div');
          highlightEl.className = 'highlight-text';
          highlightEl.style.position = 'absolute';
          highlightEl.style.backgroundColor = 'rgba(255, 217, 0, 0.5)'; // Brighter yellow with more opacity
          highlightEl.style.mixBlendMode = 'multiply';
          highlightEl.style.border = '2px solid rgba(43, 0, 255, 0.8)'; // Orange border with high opacity
          highlightEl.style.borderRadius = '2px';
          highlightEl.style.transition = 'all 0.2s ease-in-out';
          highlightEl.style.pointerEvents = 'none';
          highlightEl.style.zIndex = '1';

          // Add subtle box shadow for more emphasis
          highlightEl.style.boxShadow = '0 0 4px rgba(255, 165, 0, 0.3)';

          const rect = element.getBoundingClientRect();
          const containerRect = textLayer.getBoundingClientRect();

          highlightEl.style.left = `${rect.left - containerRect.left}px`;
          highlightEl.style.top = `${rect.top - containerRect.top}px`;
          highlightEl.style.width = `${rect.width}px`;
          highlightEl.style.height = `${rect.height}px`;

          textLayer.appendChild(highlightEl);
        });
      }
    });

    setHighlightedArea(area);
  };


  // Update useEffect to transform items when notes or careerCards change
  useEffect(() => {
    // Transform even if empty arrays
    const transformedNotes = (notes || []).map(note => transformToFilterItem(note, 'note'));
    const transformedCards = (careerCards || []).map(card => transformToFilterItem(card, 'card'));
    
    // Extract tags using the utility
    const allItems = [...transformedNotes, ...transformedCards];
    const extractedTags = extractTags(allItems, {
      notes: {
        customTags: true,
        dateTags: true,
      },
      cards: {
        customTags: true,
        dateTags: true,
      },
    });
    
    setEnhancedFilterItems({
      notes: transformedNotes,
      cards: transformedCards
    });
  }, [notes, careerCards]);

  /**
   * Processes filter changes from the EnhancedFilterModal
   * Updates selected notes and career cards for context
   */
  const handleFilterChange = (selectedNotes: FilterItem[], selectedCards: FilterItem[]) => {
    // Update the selected notes and cards IDs
    const noteIds = selectedNotes.map(note => note.id);
    const cardIds = selectedCards.map(card => card.id);
    
    setSelectedNotes(noteIds);
    setSelectedCareerCards(cardIds);
  };

  /**
   * Finds and highlights text matches in the resume
   * Supports partial and exact matches with context
   */
  const findTextInElement = (container: HTMLElement | null, searchText: string): HTMLElement[] => {
    if (!container || !searchText || searchText.length < 2) return [];

    const cleanedSearchText = cleanTextForMatching(searchText);
    const textElements = Array.from(container.querySelectorAll('span[data-text]'));
    const matches: HTMLElement[] = [];

    textElements.forEach(element => {
      const elementText = cleanTextForMatching(element.getAttribute('data-text') || '');
      if (elementText.includes(cleanedSearchText)) {
        matches.push(element as HTMLElement);
      }
    });

    return matches;
  };

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Resumes"
      />
      <div className="flex-1 overflow-y-auto" style={{ paddingTop: `${PAGE_HEADER_HEIGHT}px` }}>
        <div className="p-4">
          <div className="max-w-[1800px] mx-auto">
            {/* Upload Section */}
            <div className="mb-8">
              <button
                onClick={() => setSectionsVisible(prev => ({ ...prev, upload: !prev.upload }))}
                className="w-full flex items-center justify-between text-xl font-semibold mb-4 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
              >
                <span>Upload Resume</span>
                {sectionsVisible.upload ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
              </button>
              <div className={`transition-all duration-300 ease-in-out ${sectionsVisible.upload ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                <div
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={`p-8 border-2 border-dashed rounded-lg mb-4 text-center transition-colors ${
                    darkMode
                      ? isDragging
                        ? 'border-blue-500 bg-gray-800'
                        : 'border-gray-600 bg-gray-800'
                      : isDragging
                      ? 'border-blue-500 bg-gray-50'
                      : 'border-gray-300 bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col items-center justify-center">
                    <IoCloudUploadOutline className="w-12 h-12 mb-4 text-gray-400" />
                    <p className="mb-2 text-lg">
                      {isDragging ? 'Drop your file here' : 'Drag and drop your resume here'}
                    </p>
                    <p className="text-sm text-gray-500 mb-4">or</p>
                    <div className="flex gap-4">
                      <button
                        onClick={() => document.getElementById('file-upload')?.click()}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 transition-colors"
                      >
                        <IoCloudUploadOutline className="w-5 h-5" />
                        Upload from Computer
                      </button>
                    </div>
                  </div>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mb-8">
              <button
                onClick={() => setSectionsVisible(prev => ({ ...prev, resumes: !prev.resumes }))}
                className="w-full flex items-center justify-between text-xl font-semibold mb-4 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
              >
                <span>Your Resumes</span>
                {sectionsVisible.resumes ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
              </button>
              <div className={`transition-all duration-300 ease-in-out ${sectionsVisible.resumes ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                {isLoadingResumes ? (
                  <div className="flex items-center justify-center py-8">
                    <AiOutlineLoading3Quarters className="animate-spin w-8 h-8 text-gray-400" />
                  </div>
                ) : resumes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No resumes uploaded yet
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {resumes.map((resume) => (
                      <div key={resume.id} className={`p-4 rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="w-12 h-12 shrink-0">
                            <ThumbnailPreview
                              file={resume}
                              size="sm"
                              className="w-full h-full"
                            />
                          </div>
                          <div>
                            <button
                              onClick={() => handleViewResume(resume)}
                              className="text-blue-500 hover:text-blue-600 mr-2"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteResume(resume)}
                              className="text-red-500 hover:text-red-600"
                              aria-label="Delete resume"
                              title="Delete resume"
                            >
                              <IoTrashOutline />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm truncate">{resume.file_name}</p>
                        <div className="mt-2">
                          <input
                            type="radio"
                            id={`resume-${resume.id}`}
                            name="selectedResume"
                            checked={selectedResumeForSuggestions?.id === resume.id}
                            onChange={() => setSelectedResumeForSuggestions(resume)}
                            className="mr-2"
                          />
                          <label htmlFor={`resume-${resume.id}`}>Use for suggestions</label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-8">
              <button
                onClick={() => setSectionsVisible(prev => ({ ...prev, tailor: !prev.tailor }))}
                className="w-full flex items-center justify-between text-xl font-semibold mb-4 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
              >
                <span>Tailor Resume for Job</span>
                {sectionsVisible.tailor ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
              </button>
              <div className={`transition-all duration-300 ease-in-out ${sectionsVisible.tailor ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
              <div className="mb-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="jobDescriptionType"
                    value="text"
                    checked={jobDescriptionType === 'text'}
                    onChange={() => setJobDescriptionType('text')}
                  />
                  <span className="ml-2">Paste Job Description</span>
                </label>
                <label className="inline-flex items-center ml-6">
                  <input
                    type="radio"
                    className="form-radio"
                    name="jobDescriptionType"
                    value="url"
                    checked={jobDescriptionType === 'url'}
                    onChange={() => setJobDescriptionType('url')}
                  />
                  <span className="ml-2">Enter Job Description URL</span>
                  <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-blue-500 rounded-full">Beta</span>
                </label>
              </div>
              {jobDescriptionType === 'text' ? (
                <textarea
                  value={jobDescriptionText}
                  onChange={(e) => setJobDescriptionText(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      if (selectedResumeForSuggestions && !isGeneratingSuggestions) {
                        handleGetSuggestions();
                      }
                    }
                  }}
                  placeholder="Paste the job description here... (Ctrl/Cmd + Enter to get suggestions)"
                  className={`w-full p-2 mb-4 border rounded ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}
                  rows={4}
                />
              ) : (
                <div>
                  <input
                    type="url"
                    value={jobDescriptionUrl}
                    onChange={(e) => {
                      setJobDescriptionUrl(e.target.value);
                      setJobUrlError(null);
                    }}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                        e.preventDefault();
                        if (!isParsing) {
                          handleParseJobUrl();
                        }
                      }
                    }}
                    placeholder="Enter the job description URL... (Ctrl/Cmd + Enter to parse)"
                    className={`w-full p-2 mb-4 border rounded ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}
                  />
                    <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={handleParseJobUrl}
                        className={`bg-blue-500 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2 ${
                          isParsing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
                        }`}
                        disabled={isParsing}
                      >
                        {isParsing && <AiOutlineLoading3Quarters className="animate-spin" />}
                        {isParsing ? 'Parsing...' : 'Parse URL'}
                  </button>
                      {parsingStatus && (
                        <span className="text-sm text-gray-600 animate-pulse">
                          {parsingStatus}
                        </span>
                      )}
                    </div>
                    {jobUrlError && (
                      <div className="text-red-500 mb-4">
                        {jobUrlError}
                      </div>
                    )}
                  {parsedJobDescription && (
                    <div className="mb-4">
                      <h3 className="font-semibold mb-2">Parsed Job Description:</h3>
                      <textarea
                        value={parsedJobDescription}
                        onChange={(e) => setParsedJobDescription(e.target.value)}
                        className={`w-full p-2 border rounded ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}
                        rows={6}
                        aria-label="Parsed job description"
                        title="Parsed job description"
                      />
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={handleGetSuggestions}
                  className={`bg-blue-500 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2 ${
                    (!selectedResumeForSuggestions || (jobDescriptionType === 'url' && !parsedJobDescription) || isGeneratingSuggestions) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
                  }`}
                  disabled={!selectedResumeForSuggestions || (jobDescriptionType === 'url' && !parsedJobDescription) || isGeneratingSuggestions}
                >
                  {isGeneratingSuggestions && <AiOutlineLoading3Quarters className="animate-spin" />}
                  {isGeneratingSuggestions ? 'Generating...' : 'Get Suggestions'}
              </button>
                {suggestionsStatus && (
                  <span className="ml-3 text-sm text-gray-600 animate-pulse">
                    {suggestionsStatus}
                  </span>
                )}
              </div>
            </div>

            {suggestions && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Resume Analysis</h2>
                <div className="grid grid-cols-2 gap-6">
                  {/* Resume Preview */}
                  <div className="h-[800px] relative pb-8">
                    {selectedResumeForSuggestions ? (
                      <div className={`w-full h-full rounded relative ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        {selectedResumeForSuggestions.mime_type === 'application/pdf' ? (
                          signedResumeUrl ? (
                            renderPdfViewer()
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <AiOutlineLoading3Quarters className="animate-spin w-8 h-8 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">Loading resume preview...</p>
                              </div>
                            </div>
                          )
                        ) : (
                          // For DOC/DOCX files, show download button with signed URL
                          (<div className="flex flex-col items-center justify-center h-full">
                            <IoDocumentTextOutline className="w-16 h-16 text-gray-400 mb-4" />
                            <p className="mb-4 text-center">
                              This file format ({selectedResumeForSuggestions.mime_type}) cannot be displayed directly in the browser.
                            </p>
                            {signedResumeUrl ? (
                              <a
                                href={signedResumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                              >
                                Open Resume in New Tab
                              </a>
                            ) : (
                              <div className="text-center">
                                <AiOutlineLoading3Quarters className="animate-spin w-6 h-6 text-gray-400 mx-auto mb-2" />
                                <p className="text-gray-500">Generating download link...</p>
                              </div>
                            )}
                          </div>)
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center text-gray-500">
                          <p>No resume selected for suggestions</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Replace the old suggestions content with the new component */}
                  <div className={`overflow-y-auto h-[800px]`}>
                    <SuggestionsPanel
                      suggestions={suggestions}
                      onHighlight={handleHighlight}
                      darkMode={darkMode}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {selectedResume && selectedResumeUrl && (
        <FileViewerModal
          isOpen={isResumeModalOpen}
          onClose={() => setIsResumeModalOpen(false)}
          fileUrl={selectedResumeUrl}
          mimeType={selectedResume.mime_type}
        />
      )}
      <PersistentFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        title="Select Additional Context"
        initialNotes={(notes || []).map(note => transformToFilterItem(note, 'note'))}
        initialCards={(careerCards || []).map(card => transformToFilterItem(card, 'card'))}
        onFilterChange={handleFilterChange}
        onSubmit={handleFilterModalSubmit}
        typeVisibility={{
          notes: true,
          careerCards: true
        }}
      />
    </div>
  );
};

export default Resumes;